import { Button } from '@brightdrop/bd-ui';
import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { cx } from '@emotion/css';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  type BatchBoardingVehicleSummary,
  useGetBatchSessionsByIdQuery,
} from '~/api/generated-api';
import { useAppDispatch } from '~/app/store';
import CloseIcon from '~/assets/icons/new/close.svg?react';
import RejectedIcon from '~/assets/icons/new/error.svg?react';
import SuccessIcon from '~/assets/icons/new/successCircle.svg?react';
// components
import FullscreenExpandIcon from '~/assets/icons/new/video-controls/fullscreen-sm.svg?react';
import FullscreenCollapseIcon from '~/assets/icons/new/video-controls/fullscreenExit-sm.svg?react';
import { LoadingEllipses } from '~/common/components/LoadingEllipses/LoadingEllipses';
// utils
import TranslatedLabel, {
  translateText,
} from '~/common/components/TranslatedLabel/TranslatedLabel';
import { DATE_TIME_FORMAT_WITH_SEPARATED_TIMEZONE } from '~/common/constants/common.constant';
import { useLocalizedDateTime } from '~/common/localization/useLocalizedFormatters';
import { BatchBoardingUploadType } from '~/common/models/batchBoarding.model';
import { VEHICLE_BOARDING_ENHANCEMENTS_FLAG } from '~/common/models/featureFlags.model';
import { RouteName } from '~/common/models/route.model';
import { refreshDashboardVehicleData } from '~/common/utils/dashboard-refresh.utils';
import { formatToDateTimeWithTimezone } from '~/common/utils/date-time.utils';
import { findRoute, makeRoutePath } from '~/common/utils/route/route.utils';
import { FailedVehiclesModal } from '~/features/assets/failedVehicles/FailedVehiclesModal';
// styles
import useStyles from './BatchUploadProgressWidget.styles';

interface BatchUploadProgressWidgetProps extends BatchBoardingVehicleSummary {
  organizationsId: string;
  onClose: () => void;
}

export const BatchUploadProgressWidget = ({
  id: batchId = '',
  organizationsId,
  totalVehicleCount = 0,
  completedCount = 0,
  rejectedCount = 0,
  failedCount = 0,
  reviewCount = 0,
  canceledCount = 0,
  otherCount = 0,
  startedDate = '',
  uploadType = BatchBoardingUploadType.MANUAL,
  onClose = () => {},
  fleetId,
}: BatchUploadProgressWidgetProps) => {
  // Subscribe to i18n so imperative `translateText(...)` updates after locale changes.
  useTranslation(['common']);
  const { classes } = useStyles();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { getFlag } = useFeatureFlags();
  const formatDateTime = useLocalizedDateTime();
  const isVehicleBoardingEnhancementsEnabled = getFlag(
    VEHICLE_BOARDING_ENHANCEMENTS_FLAG
  );

  const isAutoOnboarding =
    uploadType === BatchBoardingUploadType.AUTO_ONBOARDING;

  // The finished processing count should not exceed the total count.
  //   - completedCount : the amount completed successfully
  //   - rejectedCount  : the amount rejected due to validation errors
  //   - entriesFailedCount    : the amount that failed due to system errors
  const finishedCount = Math.min(
    totalVehicleCount,
    completedCount +
      rejectedCount +
      failedCount +
      reviewCount +
      canceledCount +
      otherCount
  );
  // If the total vehicle count is 0, we consider the batch as initializing.
  const isPending =
    finishedCount < totalVehicleCount || totalVehicleCount === 0;
  const isSingleUpload = totalVehicleCount === 1;
  const isBatchSuccessful =
    !isPending && completedCount + reviewCount === totalVehicleCount;

  // Auto onboarding imports are not reviewed per batch: a clean run hands off to
  // the refreshed dashboard, and anything else goes to boarding history so the
  // destination matches the multi-batch banner.
  const viewResultsLink = useMemo(() => {
    if (isAutoOnboarding) {
      return makeRoutePath(
        findRoute(
          isBatchSuccessful
            ? RouteName.ORGANIZATIONS_HOME
            : RouteName.ORGANIZATIONS_VEHICLE_BOARDING_HISTORY
        ),
        { organizationsId }
      );
    }

    return makeRoutePath(findRoute(RouteName.ORGANIZATIONS_VEHICLES_BATCH), {
      organizationsId,
      batchId,
    });
  }, [batchId, isAutoOnboarding, isBatchSuccessful, organizationsId]);

  // Control progress widget snackbar expansion
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (!isPending) {
      setIsExpanded(true);
    }
  }, [isPending]);

  const handleChange = () => {
    // For completed batches, we do not want to allow expansion/collapse.
    if (!isPending) {
      return;
    }

    // Otherwise, toggle expansion state.
    setIsExpanded(!isExpanded);
  };

  const renderAccordionIcon = () => {
    // For completed batches, we want to show a close icon instead of expansion icons.
    if (!isPending) {
      return (
        <button
          type="button"
          onClick={onClose}
          aria-label={translateText('common:closeBatchProgressWidget')}
          className={classes.closeButton}
        >
          <CloseIcon />
        </button>
      );
    }

    // For pending batches, show expansion/collapse icons.
    return isExpanded ? <FullscreenCollapseIcon /> : <FullscreenExpandIcon />;
  };

  // Should format the started date into human readable date (ex. 10/15/2025 at 02:53 PM CDT)
  const getFormattedStartedDate = () => {
    const localized = formatDateTime?.(startedDate);
    if (localized) return localized;
    try {
      return formatToDateTimeWithTimezone(
        new Date(startedDate),
        DATE_TIME_FORMAT_WITH_SEPARATED_TIMEZONE
      );
    } catch (_error) {
      return 'N/A';
    }
  };

  // Format the upload type for description
  // e.g. 'MANUAL' -> 'Manual', 'FILE' -> 'File'
  const formattedUploadType =
    uploadType.charAt(0).toUpperCase() + uploadType.slice(1).toLowerCase();

  // Fetch and parse reason code for single upload rejection/incompletion
  const { data: batchSessionData } = useGetBatchSessionsByIdQuery(
    {
      'cs-organization': organizationsId,
      ...(fleetId && { 'cs-fleet': fleetId }),
      batchId,
    },
    {
      skip: !organizationsId || !batchId || isPending || !isSingleUpload,
    }
  );

  const getResultReason = (
    defaultStatus: 'rejected' | 'incomplete' | 'unableToAdd'
  ) => {
    const reasonCode = batchSessionData?.result?.items?.[0]?.reasonCode;
    if (isSingleUpload && reasonCode) {
      return translateText(
        `asset:batchSummaryView.batchSummaryList.reasonCode.${reasonCode}`
      );
    }
    return translateText(`common:${defaultStatus}`);
  };

  const getProgressTitle = () => {
    // Return pending state
    if (isPending || !batchId) {
      return (
        <>
          {translateText('asset:batch.uploadProgressWidget.titlePending', {
            count: totalVehicleCount,
          })}{' '}
          <LoadingEllipses />
        </>
      );
    }

    // Return completed state
    return translateText('asset:batch.uploadProgressWidget.titleCompleted', {
      count: totalVehicleCount,
    });
  };

  const totalAddedVehicleCount = completedCount + reviewCount;
  const totalFailedVehicleCount =
    rejectedCount + failedCount + otherCount + canceledCount;

  const [isSnackbarOpen, setIsSnackbarOpen] = useState<boolean>(true);
  const [isViewResultsDialogOpen, setIsViewResultsDialogOpen] =
    useState<boolean>(false);

  const handleViewResults = () => {
    setIsSnackbarOpen(false);
    setIsViewResultsDialogOpen(true);
  };

  const handleCloseViewResults = () => {
    setIsViewResultsDialogOpen(false);
    onClose();
  };

  // Auto onboarding always offers a terminal CTA and always navigates, so the
  // failed-vehicles modal stays reserved for manual and file uploads.
  const shouldShowViewResults =
    isAutoOnboarding || (!isBatchSuccessful && !isSingleUpload);

  // Terminal completion already refreshes, but the dashboard projections can lag
  // behind it, and clicking while already on the dashboard would not remount the
  // card. Refreshing here gives the counts a second chance to catch up.
  const navigateToViewResults = () => {
    if (isAutoOnboarding) {
      refreshDashboardVehicleData(dispatch);
    }

    onClose();
    navigate(viewResultsLink);
  };

  const handleViewResultsClick = () => {
    if (isAutoOnboarding) {
      navigateToViewResults();

      return;
    }

    handleViewResults();
  };

  const vehicleBoardingEnhancementsContent = () => {
    return (
      <>
        {isSnackbarOpen && (
          <Accordion
            expanded={isExpanded}
            className={classes.container}
            onChange={handleChange}
            square
          >
            <AccordionSummary
              expandIcon={renderAccordionIcon()}
              className={cx(classes.summary, {
                [classes.summaryDisabled]: !isPending,
              })}
            >
              {getProgressTitle()}
            </AccordionSummary>
            {!!totalVehicleCount && (
              <AccordionDetails className={classes.details}>
                {isPending ? (
                  <>
                    <TranslatedLabel
                      typographyVariant="caption_medium"
                      translationKey="asset:batch.uploadProgressWidget.description"
                      variables={{
                        formattedStartedDate: getFormattedStartedDate(),
                        uploadType: formattedUploadType,
                      }}
                    />
                    {!isSingleUpload && (
                      <Box className={classes.progressBarContainer}>
                        <Box className={classes.progressBar}>
                          <Box
                            className={classes.progressBarFill}
                            width={`${Math.floor((finishedCount / totalVehicleCount) * 100)}%`}
                          />
                        </Box>
                        <Typography variant="small_body_regular">
                          {finishedCount}/{totalVehicleCount}
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box className={classes.viewResultsButtonContainer}>
                    {(!!completedCount || !!reviewCount) && (
                      <span className={classes.resultsDescriptionContainer}>
                        <SuccessIcon />
                        <Typography variant="caption_regular">
                          {totalAddedVehicleCount}{' '}
                          {translateText('common:added')}
                        </Typography>
                      </span>
                    )}
                    {(!!rejectedCount ||
                      !!failedCount ||
                      !!otherCount ||
                      !!canceledCount) && (
                      <span className={classes.resultsDescriptionContainer}>
                        <RejectedIcon />
                        <Typography variant="caption_regular">
                          {totalFailedVehicleCount}{' '}
                          {getResultReason('unableToAdd')}
                        </Typography>
                      </span>
                    )}
                    {shouldShowViewResults && (
                      <Button
                        size="medium"
                        width="100%"
                        onClick={handleViewResultsClick}
                      >
                        {translateText('common:viewResults')}
                      </Button>
                    )}
                  </Box>
                )}
              </AccordionDetails>
            )}
          </Accordion>
        )}
        <FailedVehiclesModal
          isOpen={isViewResultsDialogOpen}
          onClose={handleCloseViewResults}
          organizationsId={organizationsId}
          batchId={batchId}
        />
      </>
    );
  };

  return isVehicleBoardingEnhancementsEnabled ? (
    vehicleBoardingEnhancementsContent()
  ) : (
    <Accordion
      expanded={isExpanded}
      className={classes.container}
      onChange={handleChange}
      square
    >
      <AccordionSummary
        expandIcon={renderAccordionIcon()}
        className={cx(classes.summary, {
          [classes.summaryDisabled]: !isPending,
        })}
      >
        {getProgressTitle()}
      </AccordionSummary>
      {!!totalVehicleCount && (
        <AccordionDetails className={classes.details}>
          {isPending ? (
            <>
              <TranslatedLabel
                typographyVariant="caption_medium"
                translationKey="asset:batch.uploadProgressWidget.description"
                variables={{
                  formattedStartedDate: getFormattedStartedDate(),
                  uploadType: formattedUploadType,
                }}
              />
              {!isSingleUpload && (
                <Box className={classes.progressBarContainer}>
                  <Box className={classes.progressBar}>
                    <Box
                      className={classes.progressBarFill}
                      width={`${Math.floor((finishedCount / totalVehicleCount) * 100)}%`}
                    />
                  </Box>
                  <Typography variant="small_body_regular">
                    {finishedCount}/{totalVehicleCount}
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <Box className={classes.viewResultsButtonContainer}>
              {(!!completedCount || !!reviewCount) && (
                <span className={classes.resultsDescriptionContainer}>
                  <SuccessIcon />
                  <Typography variant="caption_regular">
                    {totalAddedVehicleCount} {translateText('common:added')}
                  </Typography>
                </span>
              )}
              {(!!rejectedCount ||
                !!failedCount ||
                !!otherCount ||
                !!canceledCount) && (
                <span className={classes.resultsDescriptionContainer}>
                  <RejectedIcon />
                  <Typography variant="caption_regular">
                    {totalFailedVehicleCount} {getResultReason('unableToAdd')}
                  </Typography>
                </span>
              )}
              <Button
                size="medium"
                width="100%"
                onClick={navigateToViewResults}
              >
                {translateText('common:viewResults')}
              </Button>
            </Box>
          )}
        </AccordionDetails>
      )}
    </Accordion>
  );
};
